import { Box, Button, Card, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { useFetchHealthcareProviderInformation } from 'requests/hooks/useFetchHealthcareProviderInformation';
import { useUpdateHealthcareProviderInformation } from 'requests/hooks/useUpdateHealthcareProviderInformation';
import { Organisation } from 'requests/nswag/atlas/AtlasClient';
import { HealthcareUnitResponse } from 'requests/nswag/nll/NLLClient';
import { useSnackbar } from 'notistack';
import NLLHealthcareUnitListItem from './NLLHealthcareUnitListItem';
import LoadingScreen from 'Components/LoadingScreen';
import _ from 'lodash';
import { createUpdateRequest } from './NLLHelper';
import { useCustomerContext } from 'requests/hooks/useCustomerContext';
import { useFetchCustomer } from 'requests/hooks/useFetchCustomer';

const NLLView: React.FC = () => {
  const customer = useCustomerContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: customerData,
    isFetching: isFetchingCustomerData,
    isError: isErrorFetchingCustomerData,
  } = useFetchCustomer(customer.id);
  const {
    data: healthcareProviderInformationData,
    isFetching: isFetchingHealthcareProviderInformationData,
    isError: isErrorFetchingHealthcareProviderInformationData,
  } = useFetchHealthcareProviderInformation(customer.id);

  const [healthcareProviderName, setHealthcareProviderName] = useState<string | null | undefined>(
    healthcareProviderInformationData?.name,
  );
  const [healthcareUnits, setHealthcareUnits] = useState<HealthcareUnitResponse[] | undefined>(
    healthcareProviderInformationData?.healthcareUnits,
  );

  const healthcareProviderInformationMutation = useUpdateHealthcareProviderInformation(customer.id);

  useEffect(() => {
    if (!isFetchingHealthcareProviderInformationData) {
      setHealthcareProviderName(healthcareProviderInformationData?.name);
      setHealthcareUnits(healthcareProviderInformationData?.healthcareUnits);
    }
  }, [healthcareProviderInformationData, isFetchingHealthcareProviderInformationData]);

  const onUpdateButtonClicked = async () => {
    if (healthcareUnits === undefined) return;

    setIsLoading(true);

    try {
      const request = createUpdateRequest(customer.id, healthcareProviderName, healthcareUnits);

      await healthcareProviderInformationMutation.mutateAsync(request).then(() => {
        enqueueSnackbar('Successfully updated NLL configuration', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      });
    } catch (error) {
      enqueueSnackbar('Something went wrong trying to update NLL configuration', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveChangesToHealthcareUnit = (organisationId: string) => {
    const currentHealthcareUnits = _.clone(healthcareUnits);
    const indexOfHealthcareUnitToRemove = healthcareUnits?.findIndex(
      (healthcareUnit) => healthcareUnit.organisationId === organisationId,
    );

    if (
      indexOfHealthcareUnitToRemove === -1 ||
      indexOfHealthcareUnitToRemove === undefined ||
      indexOfHealthcareUnitToRemove === null
    )
      return;

    currentHealthcareUnits?.splice(indexOfHealthcareUnitToRemove, 1);

    setHealthcareUnits(currentHealthcareUnits);
  };

  const onAddChangesToHealthcareUnit = (location: string, hsaId: string, organisationId: string, name: string) => {
    const currentHealthcareUnits = _.clone(healthcareUnits);
    const existingHealthcareUnit = currentHealthcareUnits?.find(
      (healthcareUnit) => healthcareUnit.organisationId === organisationId,
    );

    if (existingHealthcareUnit === undefined || existingHealthcareUnit === null) {
      currentHealthcareUnits?.push(
        new HealthcareUnitResponse({
          location: location,
          hsaId: hsaId,
          organisationId: organisationId,
          name: name,
        }),
      );
    } else {
      existingHealthcareUnit.location = location;
      existingHealthcareUnit.hsaId = hsaId;
      existingHealthcareUnit.name = name;
    }
    setHealthcareUnits(currentHealthcareUnits);
  };

  const getHealthCareUnit = useCallback(
    (organisationId: string) => {
      return healthcareUnits?.find((healthcareUnit) => healthcareUnit.organisationId === organisationId);
    },
    [healthcareUnits],
  );

  if (isFetchingHealthcareProviderInformationData || isFetchingCustomerData) return <LoadingScreen />;

  if (isErrorFetchingHealthcareProviderInformationData || isErrorFetchingCustomerData) {
    enqueueSnackbar(
      'Something went wrong trying to get nll configuration. Configuration does not have the correct state.',
      {
        variant: 'error',
        preventDuplicate: true,
        autoHideDuration: 3000,
      },
    );
  }

  return (
    <>
      {isErrorFetchingHealthcareProviderInformationData || isErrorFetchingCustomerData ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: '16px' }}>
          <Typography>
            Something went wrong trying to get nll configuration. Configuration does not have the correct state.
          </Typography>
        </Box>
      ) : (
        <Card
          sx={{ marginTop: '16px', marginBottom: '8px', padding: '16px', width: 'fit-content', justifySelf: 'center' }}
        >
          <Stack direction={'column'}>
            <Typography variant="h5">Customer</Typography>
            <ListItem>
              <ListItemText primary={customer.name} />
              <TextField
                value={healthcareProviderName}
                variant="outlined"
                label="NLL organisation name"
                onChange={(e) => setHealthcareProviderName(e.target.value)}
              />
            </ListItem>
            <Typography variant="h5">Organisations</Typography>
            <List>
              {customerData?.organisations.map((organisation: Organisation) => {
                const healthCareUnit = getHealthCareUnit(organisation.id);
                return (
                  <NLLHealthcareUnitListItem
                    key={healthCareUnit?.organisationId ?? organisation.id}
                    organisationId={organisation.id}
                    organisationName={organisation.name}
                    onAddChangesToUnit={onAddChangesToHealthcareUnit}
                    onRemovedUnit={onRemoveChangesToHealthcareUnit}
                    healthcareUnit={healthCareUnit}
                  />
                );
              })}
            </List>
            <Button
              onClick={onUpdateButtonClicked}
              loading={isLoading}
              variant="contained"
              sx={{ marginLeft: '16px', marginRight: '16px' }}
            >
              Update
            </Button>
          </Stack>
        </Card>
      )}
    </>
  );
};

export default NLLView;
